import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { fetchOfferCalculations } from "../../../api/offer-calculation";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

/**
 * Modern Calculation Selector Component
 * 
 * Features:
 * - Fast calculation fetching with debounced search
 * - Multiple selection support
 * - Responsive design for mobile and desktop
 * - Compact and user-friendly
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is open
 * @param {Array} props.selectedCalculations - Array of selected calculation objects
 * @param {Function} props.onChange - Callback when selection changes (receives array of calculations)
 * @param {Function} props.onClose - Callback when selector should close
 * @param {boolean} props.multiple - Allow multiple selection (default: false)
 * @param {Array} props.filters - Additional filters to apply
 */
export default function ModernCalculationSelector({
  open = false,
  selectedCalculations = [],
  onChange,
  onClose,
  multiple = false,
  filters = [],
  showHeader = true,
  excludeCalculationId = null, // ID of calculation to exclude from list
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [calculations, setCalculations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(
    new Set(selectedCalculations.map((c) => c.id))
  );

  const perPage = 20;

  // Update selectedIds when selectedCalculations prop changes
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedCalculations.map((c) => c.id)));
    }
  }, [selectedCalculations, open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      setCalculations([]);
    } else {
      setCalculations([]);
    }
  }, [open]);

  // Create search function that will be debounced
  const searchFunction = useCallback(async (query, page, currentFilters, excludeId) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        page: page,
        per_page: perPage,
        sortBy: "id",
        sort: "desc",
        fields: "id,title,description,calculation_type,total_amount,created_at",
        filters: [...currentFilters],
      };

      // Exclude current calculation if provided (use exclude_id parameter for simplicity)
      if (excludeId) {
        params.exclude_id = excludeId;
      }

      // Add query if provided (always include query parameter, even if empty)
      if (query !== undefined && query !== null) {
        params.query = query.trim() || undefined;
      }

      // Use fetchOfferCalculations
      const response = await fetchOfferCalculations(params);

      if (response && response.data) {
        if (page === 1) {
          setCalculations(response.data);
        } else {
          setCalculations((prev) => [...prev, ...response.data]);
        }

        setTotalCount(response.count || response.data.length);
        setHasMore(
          response.data.length === perPage &&
            (page * perPage < (response.count || 0))
        );
      } else {
        setCalculations([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching calculations:", err);
      setError("Kunne ikke laste kalkyler");
      setCalculations([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search function - use useRef to avoid recreating on every render
  const debouncedSearchRef = useRef(debounce(searchFunction, 300));

  // Update debounced function when searchFunction changes
  useEffect(() => {
    debouncedSearchRef.current.cancel();
    debouncedSearchRef.current = debounce(searchFunction, 300);
  }, [searchFunction]);

  // Memoize filters to prevent infinite loops (only recreate if filters actually change)
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Also filter out excluded calculation from displayed list (safety check)
  const filteredCalculations = useMemo(() => {
    if (!excludeCalculationId) return calculations;
    return calculations.filter(calc => calc.id !== excludeCalculationId);
  }, [calculations, excludeCalculationId]);

  // Fetch calculations when search query changes
  useEffect(() => {
    if (!open) {
      debouncedSearchRef.current.cancel();
      setIsLoading(false);
      return;
    }
    
    setCurrentPage(1);
    debouncedSearchRef.current(searchQuery || "", 1, filtersRef.current, excludeCalculationId);

    return () => {
      debouncedSearchRef.current.cancel();
    };
  }, [searchQuery, open]);

  // Load more calculations
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      debouncedSearchRef.current(searchQuery || "", nextPage, filtersRef.current, excludeCalculationId);
    }
  }, [isLoading, hasMore, currentPage, searchQuery]);

  // Toggle calculation selection
  const toggleCalculation = useCallback(
    (calculation) => {
      const newSelectedIds = new Set(selectedIds);

      if (newSelectedIds.has(calculation.id)) {
        newSelectedIds.delete(calculation.id);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
        }
        newSelectedIds.add(calculation.id);
      }

      setSelectedIds(newSelectedIds);

      // Get selected calculations - prioritize calculations from current list
      const selectedFromCurrent = calculations
        .filter((c) => newSelectedIds.has(c.id));
      const selectedFromPrevious = selectedCalculations
        .filter(
          (c) =>
            newSelectedIds.has(c.id) &&
            !calculations.find((calc) => calc.id === c.id)
        );
      const selected = [...selectedFromCurrent, ...selectedFromPrevious];

      if (onChange) {
        onChange(selected);
      }

      // Auto-close for single selection
      if (!multiple && newSelectedIds.size > 0 && onClose) {
        setTimeout(() => {
          onClose();
        }, 300);
      }
    },
    [
      selectedIds,
      calculations,
      selectedCalculations,
      multiple,
      onChange,
      onClose,
    ]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    if (onChange) {
      onChange([]);
    }
  }, [onChange]);

  // Check if calculation is selected
  const isSelected = useCallback(
    (calculationId) => {
      return selectedIds.has(calculationId);
    },
    [selectedIds]
  );

  if (!open) return null;

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple ? "Velg kalkyler" : "Velg kalkyle"}
            </h3>
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title="Fjern alle valg"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter kalkyler..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Selected count */}
          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {selectedIds.size} {selectedIds.size === 1 ? "kalkyle" : "kalkyler"} valgt
            </div>
          )}
        </div>
      )}
      
      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter kalkyler..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Calculation List */}
      <div className="flex-1 overflow-y-auto max-h-[600px]">
        {isLoading && calculations.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Laster kalkyler...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        ) : calculations.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-gray-500">
                {searchQuery ? "Ingen kalkyler funnet" : "Ingen kalkyler funnet"}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredCalculations.slice(0, 20).map((calculation) => {
              const selected = isSelected(calculation.id);
              
              return (
                <div
                  key={calculation.id}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                    selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  } ${!multiple ? "cursor-pointer hover:bg-gray-50" : ""}`}
                  onClick={!multiple ? () => toggleCalculation(calculation) : undefined}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCalculation(calculation);
                        }}
                        className="focus:outline-none"
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selected
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {selected && (
                            <CheckIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                      </button>
                    </div>

                    {/* Calculation Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {calculation.title || calculation.displayTitle || `Kalkyle #${calculation.id}`}
                      </p>
                      {calculation.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {calculation.description}
                        </p>
                      )}
                      {calculation.total_amount !== undefined && (
                        <p className="text-xs text-gray-600 mt-1 font-semibold">
                          Total: {new Intl.NumberFormat('no-NO', { style: 'currency', currency: 'NOK' }).format(calculation.total_amount || 0)}
                        </p>
                      )}
                      {calculation.calculation_type && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Type: {calculation.calculation_type}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && !isLoading && calculations.length < 20 && (
          <div className="p-4 text-center border-t border-gray-200">
            <button
              onClick={loadMore}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Last flere kalkyler
            </button>
          </div>
        )}

        {isLoading && calculations.length > 0 && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        )}
      </div>
    </div>
  );
}

